import React from "react";

/* ─── primitives ─────────────────────────────────────────────────────────── */

const Bone = ({ h = 14, w = "100%", radius = 6, style = {} }) => (
  <span
    className="placeholder"
    style={{ display: "block", height: h, width: w, borderRadius: radius, ...style }}
  />
);

const Card = ({ children, style = {} }) => (
  <div
    className="placeholder-glow"
    style={{
      background: "white",
      borderRadius: 12,
      padding: 24,
      border: "1px solid #f0f4ff",
      boxShadow: "0 4px 20px -4px rgba(0,0,0,0.05)",
      ...style,
    }}
  >
    {children}
  </div>
);

/* ─── KPI card ────────────────────────────────────────────────────────────── */

const SkeletonKpiCard = ({ accent }) => (
  <Card style={accent ? { borderLeft: `4px solid #e2e8f0` } : {}}>
    <Bone h={9}  w="55%" style={{ marginBottom: 14 }} />
    <Bone h={30} w="75%" style={{ marginBottom: 10 }} />
    <Bone h={5}            style={{ marginBottom: 8,  borderRadius: 99 }} />
    <Bone h={9}  w="45%" />
  </Card>
);

export const SkeletonKpiCards = ({ count = 4, label = true }) => (
  <div style={{ marginBottom: 28 }}>
    {label && <Bone h={9} w={90} style={{ marginBottom: 14, borderRadius: 4 }} className="placeholder" />}
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${count}, 1fr)`, gap: 24 }}>
      {Array.from({ length: count }).map((_, i) => <SkeletonKpiCard key={i} accent={i === 1} />)}
    </div>
  </div>
);

/* ─── chart block ─────────────────────────────────────────────────────────── */

export const SkeletonChart = ({ height = 260, style = {} }) => (
  <div
    className="placeholder-glow"
    style={{ background: "white", borderRadius: 16, padding: 24, border: "1px solid #f0f4ff", boxShadow: "0 4px 20px -4px rgba(0,0,0,0.05)", ...style }}
  >
    <Bone h={10} w="35%" style={{ marginBottom: 6 }} />
    <Bone h={8}  w="50%" style={{ marginBottom: 20 }} />
    <Bone h={height} style={{ borderRadius: 10 }} />
  </div>
);

/* ─── donut + trends side-by-side ─────────────────────────────────────────── */

export const SkeletonDonutRow = () => (
  <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 24, marginBottom: 32 }}>
    <div className="placeholder-glow" style={{ background: "white", borderRadius: 16, padding: 24, border: "1px solid #f0f4ff", boxShadow: "0 4px 20px -4px rgba(0,0,0,0.05)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
      <Bone h={160} w={160} style={{ borderRadius: "50%", marginBottom: 16 }} />
      <Bone h={9} w="60%" style={{ marginBottom: 6 }} />
      <Bone h={8} w="40%" />
    </div>
    <SkeletonChart height={220} />
  </div>
);

/* ─── cash flow bar ───────────────────────────────────────────────────────── */

export const SkeletonCashFlow = () => (
  <div className="placeholder-glow" style={{ background: "white", borderRadius: 16, padding: 32, border: "1px solid #f0f4ff", boxShadow: "0 4px 20px -4px rgba(0,0,0,0.05)", marginBottom: 32 }}>
    <Bone h={10} w="25%" style={{ marginBottom: 20 }} />
    <Bone h={18} style={{ borderRadius: 99, marginBottom: 16 }} />
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
      {[0, 1, 2].map((i) => (
        <div key={i}>
          <Bone h={9} w="50%" style={{ marginBottom: 8 }} />
          <Bone h={22} w="70%" style={{ marginBottom: 6 }} />
          <Bone h={8} w="40%" />
        </div>
      ))}
    </div>
  </div>
);

/* ─── table skeleton ─────────────────────────────────────────────────────── */

const SkeletonTableRow = ({ indent = false }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 20px", borderBottom: "1px solid #f8fafc" }}>
    <Bone h={9} w={indent ? "30%" : "40%"} style={{ marginLeft: indent ? 20 : 0 }} />
    <div style={{ flex: 1 }} />
    <Bone h={9} w={60} />
    <Bone h={9} w={60} />
    <Bone h={9} w={60} />
    <Bone h={18} w={80} style={{ borderRadius: 99 }} />
  </div>
);

export const SkeletonTable = () => (
  <div className="placeholder-glow" style={{ background: "white", borderRadius: 16, border: "1px solid #f0f4ff", boxShadow: "0 4px 20px -4px rgba(0,0,0,0.05)", overflow: "hidden", marginBottom: 0 }}>
    <div style={{ padding: "14px 20px", borderBottom: "1px solid #f1f5f9", display: "flex", gap: 12 }}>
      {[80, 120, 90, 70].map((w, i) => <Bone key={i} h={9} w={w} />)}
    </div>
    {[false, true, true, false, true, false, true].map((indent, i) => <SkeletonTableRow key={i} indent={indent} />)}
  </div>
);

/* ─── vendor list skeleton ───────────────────────────────────────────────── */

export const SkeletonVendorList = () => (
  <div className="placeholder-glow" style={{ background: "white", borderRadius: 16, border: "1px solid #f0f4ff", boxShadow: "0 4px 20px -4px rgba(0,0,0,0.05)", overflow: "hidden" }}>
    <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid #f1f5f9" }}>
      <Bone h={12} w="40%" style={{ marginBottom: 6 }} />
      <Bone h={9}  w="25%" />
    </div>
    {Array.from({ length: 6 }).map((_, i) => (
      <div key={i} style={{ padding: "14px 24px", borderBottom: "1px solid #f8fafc", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ flex: 1 }}>
          <Bone h={10} w="60%" style={{ marginBottom: 6 }} />
          <Bone h={8}  w="35%" />
        </div>
        <Bone h={20} w={50} style={{ borderRadius: 99 }} />
      </div>
    ))}
  </div>
);

/* ─── full page skeletons ────────────────────────────────────────────────── */

export const SkeletonBudgetView = () => (
  <>
    <SkeletonKpiCards count={4} label />
    <SkeletonChart height={240} style={{ marginBottom: 32 }} />
    <SkeletonDonutRow />
    <SkeletonCashFlow />
    <div style={{ display: "grid", gridTemplateColumns: "280px 1fr", gap: 24 }}>
      <SkeletonVendorList />
      <SkeletonTable />
    </div>
  </>
);

export const SkeletonRevenueView = () => (
  <>
    <SkeletonKpiCards count={4} label />
    <SkeletonChart height={280} />
  </>
);

export const SkeletonBlockView = () => (
  <>
    {/* Hero */}
    <div className="placeholder-glow" style={{ background: "linear-gradient(135deg, #1a2942 0%, #2c3e5d 100%)", borderRadius: 16, padding: "24px 28px", marginBottom: 28 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
        <Bone h={48} w={48} style={{ borderRadius: 12, background: "rgba(255,255,255,0.12)", flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
          <Bone h={18} w="30%" style={{ marginBottom: 8, background: "rgba(255,255,255,0.15)" }} />
          <Bone h={10} w="18%" style={{ background: "rgba(255,255,255,0.1)" }} />
        </div>
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        {[90, 80, 110, 95].map((w, i) => (
          <Bone key={i} h={40} w={w} style={{ borderRadius: 20, background: "rgba(255,255,255,0.1)" }} />
        ))}
      </div>
    </div>
    <SkeletonKpiCards count={4} label={false} />
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginBottom: 28 }}>
      <SkeletonTable />
      <SkeletonChart height={260} />
    </div>
    <SkeletonKpiCards count={3} label />
    <div style={{ display: "grid", gridTemplateColumns: "280px 1fr", gap: 24 }}>
      <SkeletonVendorList />
      <SkeletonTable />
    </div>
  </>
);
